// src/lib/VideoUtilsWorkerClient.ts

import { ThumbnailFrameResult, VideoReadyResult } from "./videoUtils";

export interface ThumbnailFrameResultMessage {
    type: "thumbnailFrameResult";
    result: ThumbnailFrameResult;
}

export interface VideoTrimResultMessage {
    type: "trimResult";
    file: File;
}  

export interface DoneMessage {
    type: "done";
}

export interface ProgressMessage {
    type: "progress";
    value: number;
}

export interface ReadyMessage {
    type: "ready";
    result: VideoReadyResult;
}

export interface ErrorMessage {
    type: "error";
    message: string;
}

export type WorkerMessage =
    | ThumbnailFrameResultMessage
    | ProgressMessage
    | DoneMessage
    | ErrorMessage
    | VideoTrimResultMessage
    | ReadyMessage;

export class VideoUtilsWorkerClient {
    private worker: Worker;

    constructor() {
        this.worker = new Worker(
            new URL("./videoUtils.worker.ts", import.meta.url),
            { type: "module" }
        );
    }

    start(
        fileBuffer: ArrayBuffer,
        thumbnailCount: number,
        startTime: number,
        endTime: number,
        onThumbnail: (result: ThumbnailFrameResult) => void,
        onProgress?: (p: number) => void,
        onReady?: (result: VideoReadyResult) => void,
        onDone?: () => void,
        onError?: (msg: string) => void
    ) {
        this.worker.onmessage = (event: MessageEvent<WorkerMessage>) => {
            const msg = event.data;

            if (msg.type === "thumbnailFrameResult") {
                onThumbnail(msg.result);
            }

            if (msg.type === "progress") {
                onProgress?.(msg.value);
            }

            if (msg.type === "done") {
                onDone?.();
            }

            if (msg.type === "error") {
                onError?.(msg.message);
            }

            if (msg.type === "ready") {
                onReady?.(msg.result);
            }
        };

        this.worker.postMessage({
            type: "start",
            file: fileBuffer,
            thumbnailCount,
            startTime,
            endTime
        });
    }

    props(fileBuffer: ArrayBuffer, onReady?: (result: VideoReadyResult) => void) {
        this.worker.onmessage = (event: MessageEvent<WorkerMessage>) => {
            const msg = event.data;
            if (msg.type === "ready") {
                onReady?.(msg.result);
            }
        };

        this.worker.postMessage({
            type: "props",
            file: fileBuffer
        });
    }

    cancel() {
        this.worker.postMessage({ type: "cancel" });
    }

    terminate() {
        this.worker.postMessage({ type: "terminate" });
        this.worker.terminate();
    }
}
